
import { useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useReviews, useReviewCount } from "@/hooks/useReviews";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { formatUserDisplayName, getInitials } from "@/utils/userNameUtils";

interface ReviewSectionProps {
  providerId: string;
}

const REVIEWS_PER_PAGE = 5;

const ReviewSection = ({ providerId }: ReviewSectionProps) => {
  const { data: reviews, isLoading } = useReviews(providerId);
  const { data: reviewCount } = useReviewCount(providerId);
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate average rating from actual reviews data
  const averageRating = reviews && reviews.length > 0
    ? parseFloat((reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1))
    : 0;

  const totalPages = reviews ? Math.ceil(reviews.length / REVIEWS_PER_PAGE) : 0;
  const startIndex = (currentPage - 1) * REVIEWS_PER_PAGE;
  const paginatedReviews = reviews?.slice(startIndex, startIndex + REVIEWS_PER_PAGE) || [];

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const getAvatarColor = (userId: string | null) => {
    if (!userId) return 'bg-gray-100 text-gray-600';
    // Generate consistent colors based on user ID
    const colors = [
      'bg-blue-100 text-blue-600',
      'bg-green-100 text-green-600',
      'bg-purple-100 text-purple-600',
      'bg-pink-100 text-pink-600',
      'bg-yellow-100 text-yellow-600',
      'bg-indigo-100 text-indigo-600',
      'bg-orange-100 text-orange-600',
      'bg-teal-100 text-teal-600',
    ];
    const index = userId.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'
        }`}
      />
    ));
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-4">Reviews</h2>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex space-x-4">
              <Skeleton className="w-12 h-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold mb-4">Reviews</h2>
      
      {/* Overall Rating */}
      <div className="mb-6 pb-6 border-b border-gray-100">
        <div className="flex items-center space-x-2 mb-2">
          {renderStars(Math.round(averageRating))}
          <span className="text-2xl font-bold">{averageRating}</span>
          <span className="text-gray-500">({(reviewCount ?? 0).toLocaleString()} review{(reviewCount ?? 0) !== 1 ? 's' : ''})</span>
        </div>
        <p className="text-sm text-gray-600">Based on verified customer reviews</p>
      </div>

      {/* Individual Reviews */}
      <div className="space-y-6">
        {paginatedReviews.length > 0 ? (
          paginatedReviews.map((review) => (
            <div key={review.id} className="flex space-x-4">
              <Avatar className="w-12 h-12">
                <AvatarFallback className={`font-semibold ${getAvatarColor(review.user_id)}`}>
                  {getInitials(
                    review.profiles?.first_name || null,
                    review.profiles?.last_name || null,
                    review.user_id
                  )}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900">
                    {formatUserDisplayName(
                      review.profiles?.first_name || null,
                      review.profiles?.last_name || null,
                      review.user_id
                    )}
                  </span>
                  <span className="text-gray-500 text-sm">{formatDate(review.created_at)}</span>
                </div>
                <div className="flex items-center space-x-1 mb-2">
                  {renderStars(review.rating)}
                </div>
                {review.comment && (
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{review.comment}</p>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-500 py-8">
            <p>No reviews yet for this provider.</p>
            <p className="text-sm mt-2">Be the first to leave a review!</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-gray-100">
          <Button
            variant="outline"
            size="icon"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="h-8 w-8"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "outline"}
                size="sm"
                onClick={() => goToPage(page)}
                className="h-8 w-8 p-0"
              >
                {page}
              </Button>
            ))}
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="h-8 w-8"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReviewSection;
